import { InteractiveStatCard } from '@/components/dashboard/stats/interactive-stat-card';
import { TaskCompletionChart } from '@/components/dashboard/charts/task-completion-chart';
import { TaskDistribution } from '@/components/dashboard/charts/task-distribution';
import { PerformanceMetrics } from '@/components/dashboard/charts/performance-metrics';
import { RevenueBreakdown } from '@/components/dashboard/charts/revenue-breakdown';
import { RevenueGrowth } from '@/components/dashboard/charts/revenue-growth';
import { UserActivity } from '@/components/dashboard/charts/user-activity';
import { SystemHealth } from '@/components/dashboard/charts/system-health';
import { Users, Settings, Activity, Key, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { motion } from 'framer-motion';

export function AdminDashboard() {
  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <motion.h1 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="text-3xl font-bold tracking-tight text-primary-700 dark:text-primary-300"
        >
          Admin Dashboard
        </motion.h1>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-sm px-4 py-2 rounded-full glass-effect text-muted-foreground"
        >
          Last updated: {new Date().toLocaleString()}
        </motion.div>
      </div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
      >
        <InteractiveStatCard
          title="Total Users"
          value="156"
          description="+12 this month"
          icon={Users}
          trend={{
            data: [
              { name: 'Week 1', value: 132 },
              { name: 'Week 2', value: 140 },
              { name: 'Week 3', value: 148 },
              { name: 'Week 4', value: 156 },
            ],
            description: "User growth over the past month"
          }}
          details={{
            description: "Breakdown of user types and their status",
            items: [
              { label: "Property Managers", value: "45" },
              { label: "Property Owners", value: "78" },
              { label: "FlexHelpers", value: "33" },
              { label: "Active Users", value: "142" },
              { label: "Pending Verification", value: "8" },
              { label: "New This Month", value: "12" }
            ]
          }}
        />

        <InteractiveStatCard
          title="System Health"
          value="99.9%"
          description="Uptime this month"
          icon={Settings}
          trend={{
            data: [
              { name: '24h', value: 100 },
              { name: '48h', value: 99.9 },
              { name: '72h', value: 99.9 },
              { name: '96h', value: 100 },
            ],
            description: "System performance over the past 96 hours"
          }}
          details={{
            description: "System health metrics",
            items: [
              { label: "API Response Time", value: "45ms" },
              { label: "Active Sessions", value: "234" },
              { label: "Error Rate", value: "0.1%" },
              { label: "CPU Usage", value: "42%" }
            ]
          }}
        />

        <InteractiveStatCard
          title="Active Tasks"
          value="248"
          description="+15% this week"
          icon={Activity}
          trend={{
            data: [
              { name: 'Mon', value: 180 },
              { name: 'Tue', value: 220 },
              { name: 'Wed', value: 250 },
              { name: 'Thu', value: 280 },
              { name: 'Fri', value: 270 },
            ],
            description: "Task activity over the past week"
          }}
          details={{
            description: "Task status breakdown",
            items: [
              { label: "In Progress", value: "89" },
              { label: "Pending", value: "45" },
              { label: "Completed Today", value: "34" },
              { label: "Overdue", value: "12" }
            ]
          }}
        />

        <InteractiveStatCard
          title="System Alerts"
          value="2"
          description="Active warnings"
          icon={AlertTriangle}
          trend={{
            data: [
              { name: '1h', value: 2 },
              { name: '2h', value: 3 },
              { name: '3h', value: 2 },
              { name: '4h', value: 2 },
            ],
            description: "Alert frequency over the past 4 hours"
          }}
          details={{
            description: "Current system alerts",
            items: [
              { label: "Critical", value: "0" },
              { label: "Warnings", value: "2" },
              { label: "Resolved Today", value: "5" },
              { label: "Pending Review", value: "1" }
            ]
          }}
        />
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid gap-6 lg:grid-cols-2"
      >
        <Card variant="glass" className="overflow-hidden p-1">
          <TaskCompletionChart />
        </Card>
        <Card variant="glass" className="overflow-hidden p-1">
          <TaskDistribution />
        </Card>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid gap-6 lg:grid-cols-2"
      >
        <Card variant="glass" className="overflow-hidden p-1">
          <PerformanceMetrics />
        </Card>
        <Card variant="glass" className="overflow-hidden p-1">
          <UserActivity />
        </Card>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid gap-6 lg:grid-cols-2"
      >
        <Card variant="glass" className="overflow-hidden p-1">
          <RevenueBreakdown />
        </Card>
        <Card variant="glass" className="overflow-hidden p-1">
          <RevenueGrowth />
        </Card>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="grid gap-6"
      >
        <Card variant="glass" className="overflow-hidden p-1">
          <SystemHealth />
        </Card>
      </motion.div>
    </div>
  );
}